"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { X } from "lucide-react";
import { usePathname, useSearchParams, useRouter } from "next/navigation";
import { ChatWindow } from "./chat-window";
import { ChatMessageProps } from "./chat-message";
import { cn } from "@/lib/utils";
import { useChatContext } from "@/components/aios-assistant/context";
import { serializeCatalog } from "@/utils/route-catalog";
import { INVALIDATION_GROUPS } from "@/utils/query-keys";
import Image from "next/image";
import { useQueryClient } from "@tanstack/react-query";
import { postStream, del, fetchData } from "@/utils/api-handler";
import { toast } from "sonner";
import { useProtectedMutation } from "@/hooks/use-protected-mutation";
import { useProtectedQuery } from "@/hooks/use-protected-query";

// ─── Action types the backend tool-runner can return ──────────────────────────
//
// Backend handles ALL data mutations via its own tools.
// Frontend only needs to:
//   1. Navigate to a page/tab
//   2. Invalidate React Query keys so UI auto-refreshes
//   3. Show a toast confirmation
//
type AssistantAction =
  | { type: "navigate"; url: string }                                        // router.push
  | { type: "open_tab"; tab: string }                                        // ?tab=X
  | { type: "refresh"; keys: string[] }                                      // invalidate + refetch
  | { type: "show_toast"; variant: "success" | "error" | "info"; message: string };

// Action tokens can be multi-line JSON — match balanced braces
function extractActions(text: string): { clean: string; actions: AssistantAction[] } {
  const actions: AssistantAction[] = [];
  // Match [action]: followed by a JSON object (handles nested braces)
  const clean = text.replace(/\[action\]:\s*(\{[\s\S]*?\})/g, (_, json) => {
    try { actions.push(JSON.parse(json) as AssistantAction); } catch { /* malformed — ignore */ }
    return "";
  }).trim();
  return { clean, actions };
}

const OPEN_PREF_KEY = "aios-assistant-open";

type AiosAssistantMessage = {
  id?: string;
  created_at?: string;
  updated_at?: string;
  session_id?: string;
  content?: string;
  role?: string;
};

type AiosAssistantMessagesResponse = {
  item_count?: number;
  page_count?: number;
  items?: AiosAssistantMessage[];
};

function isUserRole(role?: string): boolean {
  const normalized = role?.trim().toUpperCase();
  return normalized === "USER";
}

function mapApiMessagesToChat(items: AiosAssistantMessage[]): ChatMessageProps[] {
  const sorted = [...items].sort((a, b) => {
    const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
    return aTime - bTime;
  });

  const mapped: ChatMessageProps[] = [];
  for (const row of sorted) {
    const text = row.content?.trim() ?? "";
    if (!text) continue;
    const isUser = isUserRole(row.role);
    mapped.push({
      id: row.id,
      message: text,
      isUser,
      timestamp: row.created_at ? new Date(row.created_at) : undefined,
      name: isUser ? undefined : "AI",
    });
  }
  return mapped;
}

async function fetchAssistantMessages(): Promise<{
  messages: ChatMessageProps[];
  sessionId: string | null;
}> {
  const params = new URLSearchParams({
    page: "1",
    limit: "50",
    sort_by: "created_at",
    sort_order: "asc",
  });
  const response = (await fetchData(
    `/api/aios-assistant/messages?${params.toString()}`
  )) as AiosAssistantMessagesResponse;
  const items = response?.items ?? [];
  const sessionId = items.find((item: AiosAssistantMessage) => item.session_id)?.session_id ?? null;

  return {
    messages: mapApiMessagesToChat(items),
    sessionId,
  };
}

export interface ChatBubbleProps {
  /**
   * Initial messages to display in the chat
   */
  initialMessages?: ChatMessageProps[];

  /**
   * Callback when a message is sent
   */
  onSendMessage?: (message: string) => void | Promise<void>;

  /**
   * Title of the chat window
   */
  title?: string;

  /**
   * Subtitle of the chat window
   */
  subtitle?: string;

  /**
   * Position of the chat bubble
   * @default "bottom-right"
   */
  position?: "bottom-right" | "bottom-left";

  /**
   * Additional className for the container
   */
  className?: string;
}

export function ChatBubble({
  initialMessages = [],
  onSendMessage,
  title = "AIOS Assistant",
  subtitle = "Always here to help",
  position = "bottom-right",
  className,
}: ChatBubbleProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { getContextualPrompts, pageContext } = useChatContext();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessageProps[]>(initialMessages);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [contextualPrompts, setContextualPrompts] = useState(getContextualPrompts());

  const {
    data: history,
    hasPermission: canUseAssistant,
    refetch: refetchHistory,
  } = useProtectedQuery({
    queryKey: ["aios-assistant-messages"],
    queryFn: fetchAssistantMessages,
    requiredPermissions: ["CREATE_AIOS_ASSISTANT_MESSAGE"],
  });

  const setOpenPreference = useCallback((open: boolean) => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem(OPEN_PREF_KEY, open ? "1" : "0");
    }
    setIsOpen(open);
  }, []);

  // Update contextual prompts when page changes (pathname only to avoid infinite loops)
  useEffect(() => {
    setContextualPrompts(getContextualPrompts());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-sync when route changes
  }, [pathname]);

  // Keep panel open across refresh when user left it open.
  useEffect(() => {
    if (typeof window !== "undefined" && sessionStorage.getItem(OPEN_PREF_KEY) === "1") {
      setIsOpen(true);
    }
  }, []);

  // Hydrate from API only when local chat is empty (avoids blink after stream ends).
  useEffect(() => {
    if (!history || isLoading) return;
    setMessages((prev) => (prev.length > 0 ? prev : history.messages));
    setSessionId((prev) => prev ?? history.sessionId);
  }, [history, isLoading]);

  useEffect(() => {
    if (isOpen) {
      void refetchHistory();
    }
  }, [isOpen, refetchHistory]);

  const streamedTextRef = useRef("");

  // ── Execute actions returned by the backend ──────────────────────────────
  // Backend tools handle all data mutations. Frontend only navigates + refreshes.
  const executeActions = useCallback(async (actions: AssistantAction[]) => {
    for (const action of actions) {
      try {
        switch (action.type) {
          case "navigate":
            router.push(action.url);
            break;

          case "open_tab":
            router.push(`${pathname}?tab=${action.tab}`);
            break;

          case "refresh":
            // Invalidate each key so React Query re-fetches and UI updates instantly
            action.keys.forEach((key) =>
              queryClient.invalidateQueries({ queryKey: [key] })
            );
            break;

          case "show_toast":
            if (action.variant === "success") toast.success(action.message);
            else if (action.variant === "error") toast.error(action.message);
            else toast.info(action.message);
            break;
        }
      } catch {
        // best-effort — never crash the chat
      }
    }
  }, [router, pathname, queryClient]);

  const clearSessionMutation = useProtectedMutation({
    mutationFn: () => del("/api/aios-assistant/sessions"),
    requiredPermissions: ["DELETE_AIOS_ASSISTANT_SESSION"],
    onSuccess: () => {
      setSessionId(null);
      setMessages([]);
      streamedTextRef.current = "";
      queryClient.setQueryData(["aios-assistant-messages"], {
        messages: [],
        sessionId: null,
      });
    },
    onError: () => {
      toast.error("Failed to clear chat");
    },
    showToast: false,
  });

  const buildUiContext = useCallback(() => {
    const segments = pathname?.split("/").filter(Boolean) ?? [];
    const entityId = segments.length >= 1 ? segments[segments.length - 1] : "";
    const entityType = segments.length >= 2 ? segments[segments.length - 2] : "";
    const currentUrl =
      (typeof window !== "undefined" && window.location?.href) || pathname || "";

    // Derive a human-readable page name from the URL
    const pageNameMap: Record<string, string> = {
      dashboard: "Dashboard",
      users: "Users",
      settings: "Settings",
      billing: "Billing",
      integrations: "Integrations",
      documents: "Documents",
      help: "Help",
      blueprints: "Blueprints",
      leads: "Leads",
      customers: "Customers",
      "sales-agents": "Sales Agent",
      "support-agents": "Support Agent",
      "sales-activity-log": "Sales Activity Log",
      "sales-personas": "Sales Personas",
      "analyst-agent": "Analyst Agent",
      "computer-use-agent": "Computer Use Agent",
      agents: "Command Agent",
    };
    const pageName = segments.reduce<string>((found, seg) => {
      return found || pageNameMap[seg] || "";
    }, "") || segments[segments.length - 1] || "Unknown";

    const pageData: Record<string, unknown> = {
      page_name: pageName,
      page_type: pageContext.pageType || "unknown",
    };

    // Auto-capture every URL query param (page, tab, search, sort, filters etc.)
    const urlParams: Record<string, string> = {};
    searchParams?.forEach((value, key) => { urlParams[key] = value; });
    if (Object.keys(urlParams).length > 0) pageData.url_params = urlParams;

    // Merge manually-set page context on top (wins over auto-detected)
    if (pageContext.filters && Object.keys(pageContext.filters).length > 0) {
      pageData.filters = pageContext.filters;
    }
    if (pageContext.stats && Object.keys(pageContext.stats).length > 0) {
      pageData.stats = pageContext.stats;
    }
    if (pageContext.activeTab) pageData.active_tab = pageContext.activeTab;
    // Also check URL for tab param if not set manually
    else if (urlParams.tab) pageData.active_tab = urlParams.tab;

    if (pageContext.searchQuery) pageData.search_query = pageContext.searchQuery;
    else if (urlParams.search) pageData.search_query = urlParams.search;

    if (pageContext.tableData) pageData.table_state = pageContext.tableData;
    else if (urlParams.page) pageData.table_state = { currentPage: Number(urlParams.page) };

    if (pageContext.selectedItems?.length) pageData.selected_items = pageContext.selectedItems;
    if (pageContext.dateRange) pageData.date_range = pageContext.dateRange;

    // Collect active React Query cache entries — snapshot of what APIs are loaded on this page
    try {
      const SORT_FIELDS = new Set(["created_at", "updated_at", "name", "title", "email", "fullname", "status", "is_active", "type", "date"]);
      const SORT_ORDERS = new Set(["asc", "desc"]);
      const EXCLUDED = new Set(["aios-assistant-messages", "llm-models", "datasources-with-groups"]);

      const labelParams = (raw: unknown[]): Record<string, unknown> => {
        const result: Record<string, unknown> = {};
        let pageAssigned = false;
        for (const p of raw) {
          if (p === null || p === undefined || p === "") continue;
          if (typeof p === "number" && !pageAssigned && p >= 1 && p <= 9999) {
            result.page = p; pageAssigned = true;
          } else if (typeof p === "string" && SORT_ORDERS.has(p)) {
            result.sort_order = p;
          } else if (typeof p === "string" && SORT_FIELDS.has(p)) {
            result.sort_by = p;
          } else if (typeof p === "boolean") {
            result.is_active = p;
          } else if (typeof p === "string" && p.length > 0) {
            result.search = p;
          } else if (typeof p === "object" && !Array.isArray(p)) {
            Object.assign(result, p);
          } else if (Array.isArray(p) && p.length > 0) {
            result.filters = p;
          }
        }
        return result;
      };

      const allQueries = queryClient.getQueryCache().getAll();
      const activeQueries = allQueries
        .filter((q) => q.state.status === "success" && q.getObserversCount() > 0)
        .map((q) => {
          const key = q.queryKey;
          const resource = Array.isArray(key) ? String(key[0]) : String(key);
          const rawParams = Array.isArray(key) && key.length > 1 ? key.slice(1) : [];
          const labeled = labelParams(rawParams);
          const entry: Record<string, unknown> = { resource };
          if (Object.keys(labeled).length > 0) entry.query_params = labeled;
          const d = q.state.data as any;
          if (d?.item_count != null) entry.total_items = d.item_count;
          else if (d?.page_count != null) entry.total_pages = d.page_count;
          return entry;
        })
        .filter((q) => {
          const r = String(q.resource);
          return !r.startsWith("_gated") && !r.startsWith("kb-page") && !EXCLUDED.has(r);
        });

      if (activeQueries.length > 0) {
        pageData.active_queries = activeQueries;
      }
    } catch {
      // Query cache read is best-effort — never block the message
    }

    return {
      current_url: currentUrl,
      page_name: pageName,
      entity_type: entityType,
      entity_id: entityId,
      page_data: pageData,
    };
  }, [pathname, searchParams, pageContext, queryClient]);

  const handleSendMessage = useCallback(
    async (message: string) => {
      const userMessage: ChatMessageProps = {
        id: `user-${Date.now()}`,
        message,
        isUser: true,
        timestamp: new Date(),
      };

      const assistantMessage: ChatMessageProps = {
        id: `assistant-${Date.now()}`,
        message: "",
        isUser: false,
        timestamp: new Date(),
        name: "AI",
      };

      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setIsLoading(true);
      streamedTextRef.current = "";

      try {
        if (onSendMessage) {
          await onSendMessage(message);
          setIsLoading(false);
          return;
        }

        const body: Record<string, unknown> = {
          content: message,
          ui_context: buildUiContext(),
        };
        // Only send catalog on the first message of a session — backend caches it
        if (!sessionId) {
          body.route_catalog = serializeCatalog();
          body.invalidation_groups = INVALIDATION_GROUPS;
        }
        if (sessionId) {
          body.session_id = sessionId;
        }

        // Buffer for incomplete JSON lines across chunks
        let lineBuffer = "";

        await postStream(
          "/api/aios-assistant/messages",
          body,
          (chunk: string) => {
            lineBuffer += chunk;

            // Split on newlines — each complete line is a JSON event
            const lines = lineBuffer.split("\n");
            // Last element may be incomplete — keep it in the buffer
            lineBuffer = lines.pop() ?? "";

            for (const line of lines) {
              const trimmed = line.trim();
              if (!trimmed) continue;

              // Try to parse as a structured event
              let event: Record<string, unknown> | null = null;
              try { event = JSON.parse(trimmed); } catch { /* plain text chunk — handle below */ }

              if (event) {
                const type = event.type as string;

                if (type === "text_delta") {
                  // ── Only text_delta is ever displayed in the conversation ──
                  streamedTextRef.current += (event.text as string) ?? "";
                  setMessages((prev) => {
                    const next = [...prev];
                    const last = next[next.length - 1];
                    if (last && !last.isUser)
                      next[next.length - 1] = { ...last, message: streamedTextRef.current };
                    return next;
                  });

                } else if (type === "navigation") {
                  // Full page navigation — build URL with optional query params
                  const path = event.path as string;
                  const query = event.query as Record<string, string> | undefined;
                  if (path) {
                    const qs = query && Object.keys(query).length
                      ? "?" + new URLSearchParams(query).toString()
                      : "";
                    void executeActions([{ type: "navigate", url: path + qs }]);
                  }

                } else if (type === "open_tab") {
                  // Switch tab in-place on the current page — no navigation
                  const tab = event.tab as string;
                  if (tab) void executeActions([{ type: "open_tab", tab }]);

                } else if (type === "data_mutations") {
                  // Backend tool completed — invalidate specified query keys
                  const keys = (event.query_keys as string[]) ?? [];
                  if (keys.length) void executeActions([{ type: "refresh", keys }]);

                } else if (type === "error") {
                  // Outer stream error — show in chat as a message
                  const errMsg = (event.error as any)?.message ?? "Something went wrong.";
                  setMessages((prev) => {
                    const next = [...prev];
                    const last = next[next.length - 1];
                    if (last && !last.isUser)
                      next[next.length - 1] = { ...last, message: errMsg };
                    return next;
                  });

                } else if (type === "session_id") {
                  setSessionId(event.session_id as string);
                }
                // All other event types are silently ignored

              } else {
                // Plain text fallback (old format) — keep backward compat
                streamedTextRef.current += trimmed;

                // Strip legacy [session_id] token
                const sessionMatch = streamedTextRef.current.match(/\[session_id\]\s*:\s*"([^"]+)"/);
                if (sessionMatch) {
                  setSessionId(sessionMatch[1]);
                  streamedTextRef.current = streamedTextRef.current
                    .replace(/\[session_id\]\s*:\s*"[^"]+"\s*/, "").trim();
                }

                // Strip legacy [action] tokens
                const { clean, actions } = extractActions(streamedTextRef.current);
                streamedTextRef.current = clean;
                if (actions.length > 0) void executeActions(actions);

                setMessages((prev) => {
                  const next = [...prev];
                  const last = next[next.length - 1];
                  if (last && !last.isUser)
                    next[next.length - 1] = { ...last, message: streamedTextRef.current };
                  return next;
                });
              }
            }
          }
        );
      } catch (error) {
        console.error("Error sending message:", error);
        toast.error("Failed to send message");
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last && !last.isUser)
            next[next.length - 1] = { ...last, message: "Sorry, something went wrong. Please try again." };
          return next;
        });
      } finally {
        setIsLoading(false);
      }
    },
    [onSendMessage, buildUiContext, sessionId, queryClient, executeActions]
  );

  const handleToggle = () => {
    setOpenPreference(!isOpen);
  };

  const handleClose = () => {
    setOpenPreference(false);
  };

  const handleClear = useCallback(() => {
    clearSessionMutation.mutate(undefined);
  }, [clearSessionMutation]);

  const positionClasses = {
    "bottom-right": "bottom-6 right-6",
    "bottom-left": "bottom-6 left-6",
  };

  // Hide chat bubble on auth pages and super-admin routes (after all hooks to satisfy rules of hooks)
  const isAuthPage =
    pathname?.includes("/auth") ||
    pathname?.includes("/login") ||
    pathname?.includes("/complete-login") ||
    pathname?.includes("/request-signup") ||
    pathname?.includes("/forgot-password") ||
    pathname?.includes("/update-forgotten-password");
  const isSuperAdminRoute = pathname?.startsWith("/super-admin");
  if (isAuthPage || isSuperAdminRoute || !canUseAssistant) {
    return null;
  }

  return (
    <div
      className={cn(
        "fixed z-50 animate-in fade-in duration-500",
        positionClasses[position],
        className
      )}
    >
      {/* Chat Window - always in DOM to avoid mount flicker on first open */}
      <div
        className={cn(
          "absolute bottom-20 right-0 w-[90vw] sm:w-[400px] h-[600px] max-h-[80vh] transition-opacity duration-200",
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none invisible"
        )}
        aria-hidden={!isOpen}
      >
        <ChatWindow
          messages={messages}
          onSendMessage={handleSendMessage}
          onClose={handleClose}
          onClear={handleClear}
          isLoading={isLoading}
          title={title}
          subtitle={subtitle}
          suggestedPrompts={contextualPrompts}
        />
      </div>

      {/* Toggle Button with Gradient */}
      <button
        onClick={handleToggle}
        style={{
          background: isOpen
            ? 'linear-gradient(to bottom right, var(--primary), var(--secondary))'
            : 'bg-transparent',
        }}
        className={cn(
          "h-14 w-14 rounded-full bg-transparent shadow-2xl transition-all duration-500 relative",
          "flex items-center justify-center",
          "hover:scale-110 active:scale-95",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          "bg-transparent",
          "cursor-pointer border-0",
          isOpen && "rotate-90"
        )}
        onMouseEnter={(e) => {
          if (!isOpen) {
            e.currentTarget.style.background = 'linear-gradient(to bottom right, var(--primary), var(--secondary))';
            e.currentTarget.style.transition = 'all 0.3s ease-in-out';
          }
        }}
        onMouseLeave={(e) => {
          if (!isOpen) {
            e.currentTarget.style.background = 'linear-gradient(to bottom right, color-mix(in srgb, var(--primary) 25%, transparent), color-mix(in srgb, var(--secondary) 25%, transparent))';
            e.currentTarget.style.transition = 'all 0.5s ease-out';
          }
        }}
      >
        {isOpen ? (
          <X className="h-6 w-6 text-white transition-all duration-300" />
        ) : (
          <div className="relative transition-all duration-300">
            <div className="h-9 w-9 rounded-full bg-transparent backdrop-blur-lg flex items-center justify-center">
              <Image
                src="/Icon_logo_light.svg"
                alt="Implement AI"
                width={24}
                height={24}
              />
            </div>
          </div>
        )}
        <span className="sr-only">{isOpen ? "Close chat" : "Open chat"}</span>
      </button>
    </div>
  );
}
